import { createPeer, closePeer } from './peer';
import { Invitation } from '@playcastdotio/Messaging';
import { queryInvitation, requestJoinInvitation } from './realtimeMessaging/handlers/invitationMessage';
import * as realtime from './realtimeMessaging';
import { deckState, invitationState, joinIdState, playerNameState } from '../stores/lobbyStores';
import { authStore, joinState, autoJoinState, loginProfileState } from '../stores/globalStores';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import { getPersistentPlayerId } from '..';
import { sendMessage } from './realtimeMessaging';
import { websocket, MatchmakingMessageType } from '@playcastdotio/Messaging';
import constants from '@playcastdotio/GlobalConfig';

export const onAlias = (invitations: Invitation[], name: string, greeting: string) => {
  deckState.updateState({
    invitations,
    name,
    greeting,
  });
  invitationState.updateState({
    ...invitationState.currentState,
    success: true,
    invites: invitations.reduce((acc, invite) => {
      acc[invite.id] = invite;
      return acc;
    }, {}),
  });
  joinState.updateState({
    ...joinState.currentState,
    state: 'chooseDeck',
  });
};

export const onAliasUpdate = (updatedInvitation: Invitation) => {
  // Check if we have it already, if so update it, if not add it.
  const invites = deckState.currentState.invitations;
  let added = false;
  const updatedInvites = invites.map((invite) => {
    if (invite.id === updatedInvitation.id) {
      added = true;
      return updatedInvitation;
    }
    return invite;
  });
  if (!added) {
    updatedInvites.push(updatedInvitation);
  }
  deckState.updateState({
    ...deckState.currentState,
    invitations: updatedInvites,
  });
};

// Utility function to wait for auth to be loaded
export const waitForAuth = async (): Promise<void> => {
  // If we already have a playerAuthId, we're ready
  if (authStore.currentState.playerAuthId) {
    return;
  }

  // Otherwise, wait for the playerAuthId to be populated
  return new Promise((resolve) => {
    const listener = authStore.addListener((state) => {
      if (state.playerAuthId) {
        authStore.removeListener(listener);
        resolve();
      }
    });

    // Set a timeout of 5 seconds in case auth never loads
    setTimeout(() => {
      authStore.removeListener(listener);
      resolve();
    }, 5000);
  });
};

// Helper function to attempt auto-join a cafe invitation
const attemptAutoJoin = async (invitationId: string): Promise<void> => {
  console.log('Attempting auto-join for invitation:', invitationId);

  // Set auto-join state to track the attempt
  autoJoinState.updateState({
    isAttempting: true,
    invitationId,
    timestamp: Date.now(),
  });

  try {
    // Wait for auth to be loaded
    await waitForAuth();

    // Send the join message
    const msg = websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_JOIN_INVITATION, {
      invitationId,
      playerInfo: {
        displayName: loginProfileState.currentState.name || 'Anonymous',
        playerId: authStore.currentState.playerAuthId || getPersistentPlayerId(),
      },
    });

    sendMessage(msg);
    console.log('Auto-join message sent for invitation:', invitationId);

    // Set a timeout to reset auto-join state if no response in 10 seconds
    setTimeout(() => {
      const currentState = autoJoinState.currentState;
      if (currentState.isAttempting && currentState.invitationId === invitationId) {
        console.log('Auto-join attempt timed out for invitation:', invitationId);
        autoJoinState.updateState({
          isAttempting: false,
          invitationId: null,
          timestamp: 0,
        });
        // Redirect to profile page since auto-join timed out
        handleRedirect(invitationId, invitationState.currentState.defaultAlias, invitationState.currentState.defaultAliasPlatform);
      }
    }, 10000);
  } catch (error) {
    console.error('Failed to attempt auto-join:', error);
    autoJoinState.updateState({
      isAttempting: false,
      invitationId: null,
      timestamp: 0,
    });
    // Redirect to profile page since auto-join failed with error
    handleRedirect(invitationId, invitationState.currentState.defaultAlias, invitationState.currentState.defaultAliasPlatform);
  }
};

export const onInviteQueryResponse = (
  invitationId: string,
  invitation?: Invitation,
  defaultAlias?: string,
  defaultAliasPlatform?: IdentityPlatform,
) => {
  console.log('Invitation query response:', invitation);
  let didAdd = false;
  if (invitation !== undefined) {
    const exists = !!invitationState.currentState.invites[invitationId];
    if (!exists) {
      didAdd = true;
    }
    invitationState.updateState({
      ...invitationState.currentState,
      invites: {
        [invitation.id]: invitation,
      },
      defaultAlias,
      defaultAliasPlatform,
    });
    // Also update active invitation if ids match
    if (invitationState.currentState.selectedInvite?.id === invitation.id) {
      invitationState.updateState({
        ...invitationState.currentState,
        selectedInvite: invitation,
        success: true,
        defaultAlias,
        defaultAliasPlatform,
      });
    }
  } else {
    // remove the invite from the list
    const invites = invitationState.currentState.invites;
    delete invites[invitationId];
    invitationState.updateState({
      ...invitationState.currentState,
      invites,
      defaultAlias,
      defaultAliasPlatform,
    });
    // set our selected invite to undefined if it was the one we were looking for
    if (invitationState.currentState.selectedInvite?.id === invitationId) {
      invitationState.updateState({
        ...invitationState.currentState,
        selectedInvite: undefined,
        success: false,
        defaultAlias,
        defaultAliasPlatform,
      });
    }
    // do the same if our joinIdState matches the invitationId
    if (joinIdState.currentState === invitationId) {
      invitationState.updateState({
        ...invitationState.currentState,
        selectedInvite: undefined,
        success: false,
        defaultAlias,
        defaultAliasPlatform,
      });
      if (!invitationState.currentState.isAlias) {
        joinState.updateState({
          ...joinState.currentState,
          state: 'chooseInvite',
        });
      }
    }
  }
  if (didAdd && !invitationState.currentState.isAlias) {
    // Variables declared outside the switch
    const persistentPlayerId = getPersistentPlayerId();

    switch (invitation?.type) {
      case 'cafe': {
        // For cafe invitations, handle player checking with auth loading considerations
        if (invitation.manifest.queueStatus === 'ready' && !invitation.invalid) {
          // This will execute the check asynchronously after auth is loaded
          (async () => {
            // Wait for auth to be fully loaded
            await waitForAuth();

            // Check if the user is in the players array
            const isUserInPlayers = invitation.manifest.players?.some(
              (player) => player.playerId === persistentPlayerId || player.playerId === authStore.currentState.playerAuthId,
            );

            if (isUserInPlayers) {
              // Don't update state if already connected
              if (joinState.currentState.state !== 'hostConnected' && joinState.currentState.state !== 'connecting') {
                joinState.updateState({
                  ...joinState.currentState,
                  state: 'connecting',
                });
              }
            } else {
              // Auto-join logic: Only attempt if invitation is matchmade to a machine (queueStatus === 'ready')
              // and there are less than 4 players in the lobby
              // And we arent errored.
              const isInErroredState =
                joinState.currentState.state === 'playerDisconnected' ||
                joinState.currentState.state === 'hostDisconnected' ||
                joinState.currentState.state === 'error';
              const currentPlayerCount = invitation.manifest.players?.length || 0;
              if (currentPlayerCount < 4 && invitation.manifest.queueStatus === 'ready' && !isInErroredState) {
                console.log(
                  `Attempting auto-join for cafe invitation ${invitation.id} (${currentPlayerCount}/4 players, queueStatus: ${invitation.manifest.queueStatus})`,
                );
                await attemptAutoJoin(invitation.id);
                // Note: We don't redirect immediately. The auto-join response will be handled
                // by the matchmaker message handler, and if it fails, we'll redirect there.
              } else {
                if (currentPlayerCount >= 4) {
                  console.log(`Lobby is full (${currentPlayerCount}/4 players), redirecting to profile`);
                } else {
                  console.log(
                    `Invitation not ready for auto-join (queueStatus: ${invitation.manifest.queueStatus}), redirecting to profile`,
                  );
                }
                handleRedirect(invitation.id, defaultAlias, invitationState.currentState.defaultAliasPlatform);
              }
            }
          })();
        } else {
          // Invitation is not ready (not matchmade to a machine) or is invalid, redirect to profile
          console.log(
            `Invitation not ready or invalid (queueStatus: ${invitation.manifest.queueStatus}, invalid: ${invitation.invalid}), redirecting to profile`,
          );
          handleRedirect(invitation.id, defaultAlias, invitationState.currentState.defaultAliasPlatform);
        }
        break;
      }
      default:
        joinState.updateState({
          ...joinState.currentState,
          state: 'chooseInvite',
        });
        break;
    }
  }
};

// Helper function to handle redirects
const handleRedirect = (invitationId: string, defaultAlias?: string, defaultAliasPlatform?: IdentityPlatform) => {
  // Save the invitation id to local storage
  localStorage.setItem('lastRedirectedInvitationId', invitationId);

  const doesUrlContainPavilion = window.location.href.includes('pavilion');

  // Redirect to different urls if we arent on pavilion
  if (!doesUrlContainPavilion) {
    if (defaultAlias) {
      switch (defaultAliasPlatform) {
        case 'clerk': {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}p/${defaultAlias}`;
          break;
        }
        case 'discord': {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}pd/${defaultAlias}`;
          break;
        }
        case 'twitch': {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}pt/${defaultAlias}`;
          break;
        }
        default: {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}p/${defaultAlias}`;
          break;
        }
      }
    } else {
      window.location.href = constants.PRIME_PLAYER_BASE_URL;
    }
    return; // Exit early for non-pavilion URLs
  }

  // If we're on pavilion, use pavilion-specific redirect logic
  if (defaultAlias) {
    // Redirect to their alias page
    switch (defaultAliasPlatform) {
      case 'clerk': {
        window.location.href = `/p/${defaultAlias}`;
        break;
      }
      case 'discord': {
        window.location.href = `/pd/${defaultAlias}`;
        break;
      }
      case 'twitch': {
        window.location.href = `/pt/${defaultAlias}`;
        break;
      }
      default: {
        window.location.href = `/p/${defaultAlias}`;
        break;
      }
    }
  } else {
    // No profile to redirect to, go to 404
    window.location.href = '/404';
  }
};

export const resolveJoinLink = async () => {
  closePeer('player:close:resolve_join_link');
  // const joinId = joinIdState.currentState;
  // queryInvitation(joinId, joinState.currentState.platform);
};

export const refreshSelectedInvitation = async () => {
  const selectedInvite = invitationState.currentState.selectedInvite;
  if (selectedInvite) {
    queryInvitation(selectedInvite.id, joinState.currentState.platform);
  }
};

export const joinInvitation = async (invitation: Invitation) => {
  const displayName = playerNameState.currentState.name;
  requestJoinInvitation(invitation.id, displayName);
};

let lastPeerConnectedTimestamp = 0;
export const connectPeer = (broadcastSessionId: string) => {
  if (Date.now() - lastPeerConnectedTimestamp < 5000) {
    console.log('Ignoring connectPeer request');
    return;
  }
  if (!broadcastSessionId) {
    console.error('No broadcast session ID provided');
    return;
  }

  // Check if already connected or connecting before changing state
  if (joinState.currentState.state === 'hostConnected') {
    console.log('Already connected, ignoring state change to connecting');
    return;
  }

  createPeer(broadcastSessionId);
  lastPeerConnectedTimestamp = Date.now();
  joinState.updateState({
    ...joinState.currentState,
    state: 'connecting',
  });
};

export const startJoin = (platform: IdentityPlatform, joinId: string) => {
  if (!joinId) {
    console.error('No join id');
    return;
  }
  console.log('Joining with id:', joinId);
  joinIdState.updateState(joinId);

  joinState.updateState({
    ...joinState.currentState,
    state: 'resolvingAlias',
    platform,
  });
};

export const initialize = () => {
  realtime.connect();
};

export const cleanup = () => {
  realtime.disconnect();
};

export const saveDisplayName = () => {
  const name = playerNameState.currentState.name;
  localStorage.setItem('name', name);
};
